<template>
  <div>
    <div class="d-flex align-center flex-wrap mb-3">
      <v-btn-toggle
        v-model="sectionFilter"
        mandatory
        dense
        class="mr-3"
      >
        <v-btn :value="0">
          All
        </v-btn>
        <v-btn :value="$enums.AUDIENCE.SECTION.EXISTING">
          Existing
        </v-btn>
        <v-btn :value="$enums.AUDIENCE.SECTION.NEW">
          New
        </v-btn>
      </v-btn-toggle>

      <v-select
        v-model="statusFilter"
        :items="statusOptions"
        multiple
        dense
        outlined
        hide-details
        clearable
        label="Status"
        class="status-filter"
      >
        <template #selection="{ item, index }">
          <v-chip
            v-if="index < 2"
            x-small
            :color="item.color"
            dark
            class="mr-1"
          >
            {{ item.label }}
          </v-chip>
          <span
            v-else-if="index === 2"
            class="text-caption grey--text"
          >+{{ statusFilter.length - 2 }}</span>
        </template>
      </v-select>

      <v-spacer />

      <span class="text-caption grey--text mr-3">{{ countLabel }}</span>

      <v-btn
        v-if="buildableCount"
        small
        outlined
        color="primary"
        :loading="building"
        :title="'Builds ' + buildableCount + ' approved item(s) into campaigns'"
        @click="buildApproved"
      >
        Build {{ buildableCount }} approved
      </v-btn>
    </div>

    <!-- The scheduler, not the plan, decides send dates — so show what it actually did. -->
    <v-alert
      v-if="results.length"
      dense
      text
      type="info"
      class="mb-3"
    >
      <div class="text-caption mb-1">
        Built {{ results.length }} campaign{{ results.length === 1 ? '' : 's' }} — the weekly caps set these
        dates:
      </div>
      <div
        v-for="r in results"
        :key="r.itemId"
        class="text-caption"
      >
        <strong>{{ r.title }}</strong>
        <span v-if="r.error"> — {{ r.error }}</span>
        <span v-else-if="r.scheduledSend"> — {{ formatDate(r.scheduledSend) }}</span>
        <span v-else> — queued</span>
        <span v-if="r.templateError" class="warning--text"> · designs not started: {{ r.templateError }}</span>
      </div>

      <!-- Rolled up rather than repeated per row: the designs are background work with no date to report. -->
      <div
        v-if="designBatchCount"
        class="text-caption mt-1"
      >
        Also started {{ designBatchCount }} design
        {{ designBatchCount === 1 ? 'batch' : 'batches' }} in the background.
      </div>
    </v-alert>

    <div
      v-if="!visible.length"
      class="text-caption grey--text"
    >
      {{ emptyText }}
    </div>

    <v-row v-else>
      <v-col
        v-for="item in visible"
        :key="item.id"
        :ref="'item-' + item.id"
        cols="12"
        md="6"
        lg="4"
      >
        <SuggestionCard
          :item="item"
          :highlight="item.id === highlightId"
          @changed="$emit('changed')"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import SuggestionCard from '@/components/marketing/audiences/SuggestionCard';
import { PLAN_ITEM_UNDER_REVIEW, planItemStatusKey } from '@/helpers/marketing';

export default {
  name: 'AudienceSuggestions',
  components: { SuggestionCard },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    hasPlan: {
      type: Boolean,
      default: false
    },
    planId: {
      type: Number,
      default: 0
    },
    // One item to single out, arrived at by clicking its touch in the plan's cadence. 0 for a normal visit.
    focusId: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      sectionFilter: 0,
      // Starts on the two statuses that still need a person: Suggested hasn't been judged, and Under Review is
      // waiting on someone to approve the draft campaign or publish the written page. Approved items are
      // already through, dismissed ones are kept only so a redraft is told not to re-propose them, and done
      // ones are the plan's history — they only accumulate, because a redraft never sweeps them — so none of
      // the three is shown until asked for, or what needs deciding gets buried. Empty means no status filter
      // at all, i.e. show everything.
      statusFilter: [this.$enums.AUDIENCE.ITEM_STATUS_IDS.SUGGESTED, PLAN_ITEM_UNDER_REVIEW],
      building: false,
      results: [],
      highlightId: 0,
      highlightTimer: null
    };
  },
  computed: {
    // Every status, in id order, with a count so the picker answers "is there anything in there?" without
    // being opened. Under Review is spliced in after Approved — it isn't a stored status, but it is what the
    // cards say, and it sits between the two in the item's life.
    statusOptions() {
      const statuses = this.$enums.AUDIENCE.ITEM_STATUS;
      const count = (key) => this.items.filter((i) => planItemStatusKey(i) === key).length;
      const options = Object.keys(statuses)
        .map(Number)
        .sort((a, b) => a - b)
        .map((id) => ({
          value: id,
          // Counted in the menu so the picker says whether a status holds anything before it is opened; the
          // chip in the closed field shows the bare label, where a count would just crowd the row.
          text: `${statuses[id].label} (${count(id)})`,
          label: statuses[id].label,
          color: statuses[id].color
        }));

      const after = options.findIndex((o) => o.value === this.$enums.AUDIENCE.ITEM_STATUS_IDS.APPROVED);
      options.splice(after + 1, 0, {
        value: PLAN_ITEM_UNDER_REVIEW,
        text: `Under Review (${count(PLAN_ITEM_UNDER_REVIEW)})`,
        label: 'Under Review',
        color: 'warning'
      });
      return options;
    },
    visible() {
      return this.items.filter((i) => {
        // An empty picker is "no status filter", not "nothing" — clearing it shows the whole plan.
        if (this.statusFilter.length && !this.statusFilter.includes(planItemStatusKey(i))) return false;
        if (this.sectionFilter && i.section !== this.sectionFilter) return false;
        return true;
      });
    },
    countLabel() {
      const ids = this.$enums.AUDIENCE.ITEM_STATUS_IDS;
      // Keyed the same way the picker is, so its counts and these add up to the same items.
      const count = (key) => this.items.filter((i) => planItemStatusKey(i) === key).length;
      // Counted off every item, not the visible ones — this is the plan's shape, and it has to keep reading
      // the same when the done rows are filtered out from under it.
      return `${count(ids.SUGGESTED)} pending · ${count(PLAN_ITEM_UNDER_REVIEW)} under review · ` +
        `${count(ids.APPROVED)} approved · ${count(ids.DONE)} done`;
    },
    emptyText() {
      if (!this.hasPlan) return 'No plan yet — generate one to get suggestions.';

      // Everything judged and finished is the plan's end state, not an empty filter. Nothing marks a plan
      // complete server-side, so this is the one place the screen can say so — and say what comes next.
      const ids = this.$enums.AUDIENCE.ITEM_STATUS_IDS;
      const outstanding = this.items.filter(
        (i) => i.status !== ids.DONE && i.status !== ids.DISMISSED
      );
      if (this.items.length && !outstanding.length) {
        return 'Everything on this plan is done. Redraft it from the Plan tab to line up the next round — ' +
          'the finished items are kept, add "Done" to the status filter to see them.';
      }

      return 'Nothing here. Try a different filter, or regenerate the plan.';
    },
    // Approved campaign items that haven't been built yet. Content items build individually.
    buildableCount() {
      const approved = this.$enums.AUDIENCE.ITEM_STATUS_IDS.APPROVED;
      return this.items.filter(
        (i) => i.status === approved && !i.campaignId && (i.itemType === 1 || i.itemType === 2)
      ).length;
    },
    // Template batches the last build started, across every item. Rolled up because they are background
    // work: unlike a send date, there is nothing per-item worth showing.
    designBatchCount() {
      return this.results.reduce((total, r) => total + (r.templatesStarted || 0), 0);
    }
  },
  watch: {
    // Both are needed: the id can arrive before the plan has loaded (a shared ?item= link), and the items can
    // arrive before the id (clicking a touch on an already-loaded plan).
    focusId: {
      immediate: true,
      handler: 'reveal'
    },
    items: 'reveal'
  },
  beforeDestroy() {
    if (this.highlightTimer) clearTimeout(this.highlightTimer);
  },
  methods: {
    // Brings the singled-out card into view — including past the filters, which the marketer did not set with
    // this item in mind and which would otherwise leave a click on the plan looking like it did nothing.
    reveal() {
      const item = this.focusId ? this.items.find((i) => i.id === this.focusId) : null;
      if (!item) return;

      if (this.sectionFilter && this.sectionFilter !== item.section) this.sectionFilter = 0;
      const key = planItemStatusKey(item);
      if (this.statusFilter.length && !this.statusFilter.includes(key)) {
        this.statusFilter = this.statusFilter.concat(key);
      }

      this.$nextTick(() => {
        const col = (this.$refs[`item-${item.id}`] || [])[0];
        if (col && col.$el) col.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Times out rather than latching: it is a "here it is" cue, and a card left permanently ringed reads
        // as a state the item is in.
        this.highlightId = item.id;
        if (this.highlightTimer) clearTimeout(this.highlightTimer);
        this.highlightTimer = setTimeout(() => {
          this.highlightId = 0;
        }, 2500);
      });
    },
    formatDate(iso) {
      return new Date(iso).toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    },
    buildApproved() {
      this.building = true;
      this.results = [];
      this.$store
        .dispatch('audiences/realizePlan', this.planId)
        .then((result) => {
          this.results = result.items || [];
          if (result.failed) {
            this.$root.$snackbarError(
              `${result.failed} item(s) could not be built — see the details above.`
            );
          } else {
            this.$root.$snackbarSuccess(`Built ${result.created} campaign(s).`);
          }
          this.$emit('changed');
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.building = false;
        });
    }
  }
};
</script>

<style scoped>
/* Keeps the picker from stretching across the filter row in the flex layout. */
.status-filter {
  max-width: 280px;
}
</style>
